

import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { PhotoIcon } from "@heroicons/react/24/outline";
import TextEditor from "../TextEditor";
import {
  createSponsor,
  updateSponsor,
} from "../../features/admin/SponsorSlice";
import { toast } from "react-toastify";

const sponsorSchema = Yup.object().shape({
  name: Yup.string().trim().required("Tên nhà tài trợ không được để trống"),
  logo: Yup.string().trim(),
  images: Yup.mixed()
    .nullable()
    .test("logo-required", "Logo không được để trống", function () {
      const { logo, images } = this.parent;
      return Boolean(logo) || images instanceof File;
    }),
  businessField: Yup.string().trim(),
  phoneNumber: Yup.string()
    .trim()
    .required("Số điện thoại không được để trống")
    .max(50, "Số điện thoại quá dài"),
  email: Yup.string().trim().email("Email không hợp lệ"),
  website: Yup.string().trim(),
  address: Yup.string().trim().required("Địa chỉ không được để trống"),
  content: Yup.string().trim(),
  priority: Yup.number()
    .typeError("Thứ tự hiển thị không hợp lệ")
    .min(0, "Thứ tự hiển thị không hợp lệ"),
  status: Yup.string().trim(),
});

const getMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_API_URL}media/${url}`;
};

const SponsorModal = ({ sponsor, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    logo: "",
    images: null,
    businessField: "",
    phoneNumber: "",
    email: "",
    website: "",
    address: "",
    content: "",
    priority: 0,
    status: "true",
  });
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (sponsor) {
      setFormData({
        name: sponsor.name || "",
        logo: sponsor.logo || "",
        images: null,
        businessField: sponsor.businessField || "",
        phoneNumber: sponsor.phoneNumber || "",
        email: sponsor.email || "",
        website: sponsor.website || "",
        address: sponsor.address || "",
        content: sponsor.content || "",
        priority: sponsor.priority ?? 0,
        status: sponsor.status === false ? "false" : "true",
      });
      setLogoPreview(sponsor.logo ? getMediaUrl(sponsor.logo) : "");
    }
  }, [sponsor]);

  const handleChange = (e) => {
    const name = e.name || e.target?.name;
    const value = e.value ?? e.target?.value;
    const files = e.target?.files;

    if (name === "images" && files?.[0]) {
      const file = files[0];
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/jpg"];

      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          images: "Chỉ chấp nhận file ảnh (JPEG, PNG, GIF)",
        }));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          images: "Kích thước file không được vượt quá 5MB",
        }));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);

      setFormData((prev) => ({ ...prev, images: file }));
      setErrors((prev) => ({ ...prev, images: undefined }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const buildSubmitData = () => {
    const submitData = new FormData();
    submitData.append("name", formData.name || "");
    submitData.append("businessField", formData.businessField || "");
    submitData.append("phoneNumber", formData.phoneNumber || "");
    submitData.append("email", formData.email || "");
    submitData.append("website", formData.website || "");
    submitData.append("address", formData.address || "");
    submitData.append("content", formData.content || "");
    submitData.append("priority", formData.priority ?? 0);
    submitData.append("status", formData.status);

    if (formData.logo) {
      submitData.append("logo", formData.logo);
    }

    if (formData.images instanceof File) {
      submitData.append("images", formData.images);
    }

    return submitData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await sponsorSchema.validate(formData, { abortEarly: false });
      setIsSubmitting(true);

      const submitData = buildSubmitData();

      if (sponsor) {
        await dispatch(
          updateSponsor({ id: sponsor.id, newSponsor: submitData })
        ).unwrap();
        toast.success("Cập nhật nhà tài trợ thành công!");
      } else {
        await dispatch(createSponsor(submitData)).unwrap();
        toast.success("Thêm nhà tài trợ thành công!");
      }

      onClose();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.messages?.name ||
              error?.messages ||
              error?.message ||
              "Đã xảy ra lỗi! Vui lòng thử lại.";

        if (errorMessage === "Tên nhà tài trợ đã tồn tại") {
          setErrors((prev) => ({
            ...prev,
            name: errorMessage,
          }));
        }

        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex p-6 sticky top-0 bg-base-100 z-10 pb-4 justify-between items-center border-b">
          <div>
            <h2 className="text-xl font-semibold">
              {sponsor ? "Chỉnh sửa nhà tài trợ" : "Thêm nhà tài trợ"}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Nhập thông tin cơ bản, logo và giới thiệu của nhà tài trợ.
            </p>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            disabled={isSubmitting}
          >
            X
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Tên nhà tài trợ<i className="text-red-600"> *</i>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Thứ tự hiển thị
                </label>
                <input
                  type="number"
                  name="priority"
                  min="0"
                  value={formData.priority}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${
                    errors.priority ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.priority && (
                  <p className="text-red-500 text-sm mt-1">{errors.priority}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Ngành nghề / lĩnh vực hoạt động
              </label>
              <textarea
                name="businessField"
                value={formData.businessField}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Số điện thoại<i className="text-red-600"> *</i>
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${
                    errors.phoneNumber ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://..."
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Trạng thái</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="true">Hiển thị</option>
                  <option value="false">Ẩn</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Địa chỉ<i className="text-red-600"> *</i>
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={`w-full p-2 border rounded ${
                  errors.address ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.address && (
                <p className="text-red-500 text-sm mt-1">{errors.address}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Logo<i className="text-red-600"> *</i>
              </label>
              <div className="flex items-center space-x-4">
                <div
                  className={`h-24 w-24 border-2 border-dashed rounded-lg overflow-hidden bg-base-100 flex items-center justify-center ${
                    errors.images ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo nhà tài trợ"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <PhotoIcon className="w-10 h-10 text-gray-300" />
                  )}
                </div>

                <input
                  type="file"
                  name="images"
                  accept="image/jpeg,image/png,image/gif,image/jpg"
                  onChange={handleChange}
                  className="file-input file-input-bordered w-full max-w-xs"
                />
              </div>
              {errors.images && (
                <p className="text-red-500 text-sm mt-1">{errors.images}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Giới thiệu
              </label>
              <TextEditor
                value={formData.content}
                onChange={handleChange}
                name="content"
                importCustomInsertImage={true}
              />
            </div>
          </div>

          <div className="flex border-t sticky bottom-0 bg-base-100 z-10 p-6 justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 btn btn-sm rounded"
              disabled={isSubmitting}
            >
              Hủy
            </button>
            <button
              type="submit"
              className={`px-4 py-2 btn btn-primary rounded btn-sm ${
                isSubmitting ? "loading" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-xs mr-2"></span>
                  Đang lưu...
                </>
              ) : (
                "Lưu"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SponsorModal;


